// Post-traitement du détecteur de cartes : cartes de probabilité (carte +
// cœur de carte, grille h x w) -> un quadrilatère par carte. Portage ligne à
// ligne de quads_from_prob (backend/scan_engine.py).

function components(core, h, w, minCells) {
  const lab = new Int32Array(h * w); let n = 0;
  for (let i = 0; i < h * w; i++) {
    if (!core[i] || lab[i]) continue;
    n += 1; const st = [i]; lab[i] = n; const cells = [];
    while (st.length) {
      const c = st.pop(); cells.push(c);
      const y = (c / w) | 0, x = c % w;
      const nb = [[y + 1, x], [y - 1, x], [y, x + 1], [y, x - 1]];
      for (const [yy, xx] of nb) {
        if (yy < 0 || yy >= h || xx < 0 || xx >= w) continue;
        const j = yy * w + xx;
        if (core[j] && !lab[j]) { lab[j] = n; st.push(j); }
      }
    }
    if (cells.length < minCells) for (const c of cells) lab[c] = -1;
  }
  for (let i = 0; i < lab.length; i++) if (lab[i] < 0) lab[i] = 0;
  return { lab, n };
}

function grow(lab, mask, h, w) {
  const out = Int32Array.from(lab); const q = [];
  for (let i = 0; i < lab.length; i++) if (lab[i]) q.push(i);
  for (let k = 0; k < q.length; k++) {
    const c = q[k], y = (c / w) | 0, x = c % w;
    const nb = [[y + 1, x], [y - 1, x], [y, x + 1], [y, x - 1]];
    for (const [yy, xx] of nb) {
      if (yy < 0 || yy >= h || xx < 0 || xx >= w) continue;
      const j = yy * w + xx;
      if (mask[j] && !out[j]) { out[j] = out[c]; q.push(j); }
    }
  }
  return out;
}

function hull(pts) {
  const uniq = new Map();
  for (const p of pts) uniq.set(`${p[0]},${p[1]}`, p);
  const s = [...uniq.values()].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (s.length < 3) return s;
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lo = [], up = [];
  for (const p of s) { while (lo.length >= 2 && cross(lo[lo.length - 2], lo[lo.length - 1], p) <= 0) lo.pop(); lo.push(p); }
  for (let i = s.length - 1; i >= 0; i--) {
    const p = s[i];
    while (up.length >= 2 && cross(up[up.length - 2], up[up.length - 1], p) <= 0) up.pop();
    up.push(p);
  }
  return lo.slice(0, -1).concat(up.slice(0, -1));
}

function area(q) {
  let s = 0;
  for (let i = 0; i < q.length; i++) { const a = q[i], b = q[(i + 1) % q.length]; s += a[0] * b[1] - b[0] * a[1]; }
  return Math.abs(s) / 2;
}

function maxQuad(hp) {
  let n = hp.length;
  if (n > 40) { hp = Array.from({ length: 40 }, (_, i) => hp[Math.floor((i * n) / 40)]); n = 40; }
  let best = -1, bq = null;
  for (let a = 0; a < n; a++) for (let b = a + 1; b < n; b++) for (let c = b + 1; c < n; c++) for (let d = c + 1; d < n; d++) {
    const q = [hp[a], hp[b], hp[c], hp[d]]; const ar = area(q);
    if (ar > best) { best = ar; bq = q; }
  }
  return bq;
}

const sub = (a, b) => [a[0] - b[0], a[1] - b[1]];
const dot = (a, b) => a[0] * b[0] + a[1] * b[1];
const norm = (a) => Math.hypot(a[0], a[1]);

function fitLines(q, pts) {
  const lines = [];
  for (let i = 0; i < 4; i++) {
    const a = q[i], b = q[(i + 1) % 4], d = sub(b, a), L = norm(d) + 1e-9;
    const nrm = [-d[1] / L, d[0] / L];
    const sel = pts.filter((p) => {
      const v = sub(p, a), t = dot(v, d) / (L * L);
      return Math.abs(dot(v, nrm)) < 0.06 * L + 1.5 && t > 0.1 && t < 0.9;
    });
    if (sel.length < 4) return q;
    const c = [sel.reduce((s, p) => s + p[0], 0) / sel.length, sel.reduce((s, p) => s + p[1], 0) / sel.length];
    let sxx = 0, sxy = 0, syy = 0;
    for (const p of sel) { const v = sub(p, c); sxx += v[0] * v[0]; sxy += v[0] * v[1]; syy += v[1] * v[1]; }
    // direction principale = vecteur propre de la plus grande valeur propre
    const tr = sxx + syy, det = sxx * syy - sxy * sxy;
    const l1 = tr / 2 + Math.sqrt(Math.max(0, (tr * tr) / 4 - det));
    let dir = Math.abs(sxy) > 1e-12 ? [l1 - syy, sxy] : (sxx >= syy ? [1, 0] : [0, 1]);
    const dn = norm(dir); dir = [dir[0] / dn, dir[1] / dn];
    lines.push([c, dir]);
  }
  const out = [];
  for (let i = 0; i < 4; i++) {
    const [p1, d1] = lines[(i + 3) % 4], [p2, d2] = lines[i];
    const det = d1[0] * -d2[1] - -d2[0] * d1[1];
    if (Math.abs(det) < 1e-6) return q;
    const r = sub(p2, p1);
    const t = (r[0] * -d2[1] - -d2[0] * r[1]) / det;
    out.push([p1[0] + t * d1[0], p1[1] + t * d1[1]]);
  }
  const lim = 0.15 * Math.sqrt(area(q));
  for (let i = 0; i < 4; i++) if (Math.abs(out[i][0] - q[i][0]) > lim || Math.abs(out[i][1] - q[i][1]) > lim) return q;
  return out;
}

function order(q) {
  const c = [(q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4, (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4];
  let s = [...q].sort((a, b) => Math.atan2(a[1] - c[1], a[0] - c[0]) - Math.atan2(b[1] - c[1], b[0] - c[0]));
  const roll = (arr, k) => arr.slice(k).concat(arr.slice(0, k));
  if (norm(sub(s[1], s[0])) > norm(sub(s[2], s[1]))) s = roll(s, 1);
  if (s[0][1] + s[1][1] > s[2][1] + s[3][1]) s = roll(s, 2);
  return s;
}

/** prob : Float32Array (2, h, w). Renvoie [{quad, score}] en coordonnées de
 * l'image d'entrée du réseau (detSize x detSize). */
export function quadsFromProb(prob, h, w, detSize, thr = 0.5, minFrac = 0.004) {
  const n = h * w, m = new Uint8Array(n), core = new Uint8Array(n);
  for (let i = 0; i < n; i++) { m[i] = prob[i] > thr ? 1 : 0; core[i] = m[i] && prob[n + i] > thr ? 1 : 0; }
  const sc = detSize / w;
  const { lab: lab0 } = components(core, h, w, Math.max(3, Math.floor(minFrac * n * 0.3)));
  const lab = grow(lab0, m, h, w);
  let maxLab = 0; for (let i = 0; i < n; i++) if (lab[i] > maxLab) maxLab = lab[i];
  const res = [];
  for (let k = 1; k <= maxLab; k++) {
    let cnt = 0, psum = 0; const pts = [];
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = y * w + x; if (lab[i] !== k) continue;
      cnt++; psum += prob[i];
      const inner = y > 0 && y < h - 1 && x > 0 && x < w - 1 &&
        lab[i - w] === k && lab[i + w] === k && lab[i - 1] === k && lab[i + 1] === k;
      if (!inner) pts.push([x + 0.5, y + 0.5]);
    }
    if (cnt < minFrac * n) continue;
    const hp = hull(pts);
    if (hp.length < 4) continue;
    const qg = order(fitLines(maxQuad(hp), pts));
    const q = qg.map(([x, y]) => [x * sc, y * sc]);
    const s1 = norm(sub(q[1], q[0])), s2 = norm(sub(q[3], q[0]));
    const ratio = Math.min(s1, s2) / Math.max(s1, s2);
    const fill = cnt / Math.max(1e-9, area(qg));
    if (ratio < 0.45 || fill < 0.6) continue;
    res.push({ quad: q, score: psum / cnt });
  }
  return res;
}

// 2ᵉ passe (cf. scan_engine.refine) : la carte est redétectée dans un
// recadrage autour d'elle, où elle occupe toute l'entrée du détecteur.
const REFINE_PAD = 0.9, REFINE_MAX = 0.6;

/** Recadrage carré borné à la photo [x0, y0, x1, y1], ou null si la carte
 * occupe déjà l'essentiel de la photo. */
export function refineBox(quad, w, h) {
  const xs = quad.map((p) => p[0]), ys = quad.map((p) => p[1]);
  const cx = xs.reduce((a, b) => a + b) / 4, cy = ys.reduce((a, b) => a + b) / 4;
  const side = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
  if (side > REFINE_MAX * Math.max(w, h)) return null;
  const r = REFINE_PAD * side;
  return [Math.max(0, Math.trunc(cx - r)), Math.max(0, Math.trunc(cy - r)),
    Math.min(w, Math.trunc(cx + r)), Math.min(h, Math.trunc(cy + r))];
}

/** La carte redétectée qui recouvre l'originale, sinon l'originale. */
export function pickRefined(quad, found) {
  const center = (q) => [q.reduce((s, p) => s + p[0], 0) / 4, q.reduce((s, p) => s + p[1], 0) / 4];
  const c = center(quad), a = area(quad);
  let best = null, bd = null;
  for (const f of found) {
    const d = norm(sub(center(f), c)), ra = area(f) / Math.max(a, 1e-9);
    if (d < 0.15 * Math.sqrt(a) && ra > 0.7 && ra < 1.4 && (bd === null || d < bd)) { best = f; bd = d; }
  }
  return best || quad;
}

/** Repli sans détection : la zone du repère de cadrage, au centre. */
export function guideQuad(w, h) {
  const ch = Math.min(h * 0.8, (w * 0.9 * 560) / 400), cw = (ch * 400) / 560;
  const x0 = (w - cw) / 2, y0 = (h - ch) / 2;
  return [[x0, y0], [x0 + cw, y0], [x0 + cw, y0 + ch], [x0, y0 + ch]];
}
